import { Screen } from '@/components/layout'
import { Button } from '@/components/primitives'
import { MetricRow, RecommendationCard, type MetricSpec } from '@/components/cards'
import { LineChart } from '@/components/charts/LineChart'
import { ProgressRing } from '@/components/charts/ProgressRing'
import { GoalRow, List } from '@/components/rows'
import {
  EmptyState,
  ErrorState,
  InsufficientData,
  LoadingRegion,
  SkeletonCard,
  SkeletonMetrics,
} from '@/components/states'
import { api } from '@/lib/api'
import { t } from '@/lib/copy'
import { formatMonthShort, formatMoney, formatPercent } from '@/lib/format'
import { Link, useNavigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'

/** Savings rate over time and progress toward an emergency fund. */
export function SavingsScreen() {
  const navigate = useNavigate()
  const cashFlow = useAsync(() => api.analysis.cashFlow(12), [])
  const recommendations = useAsync(() => api.recommendations.list({ theme: 'savings' }), [])

  const data = cashFlow.state.data
  const months = data?.months ?? []
  const complete = months.filter((m) => m.complete !== false)
  const fund = data?.emergencyFund
  const currency = data?.currency ?? 'USD'
  const latest = months.at(-1)

  const screenState =
    cashFlow.state.status === 'error'
      ? 'error'
      : cashFlow.state.status === 'loading' && !data
        ? 'loading'
        : months.length === 0
          ? 'empty'
          : complete.length < 2
            ? 'insufficientData'
            : 'ready'

  const metrics: MetricSpec[] = [
    { key: 'rate', label: t('savings.metric.rate'), value: formatPercent(data?.savingsRate, 1) },
    {
      key: 'saved',
      label: t('savings.metric.saved'),
      value: formatMoney(latest?.saved ?? { amount: 0, currency }, { absolute: true }),
    },
    { key: 'average', label: t('savings.metric.average'), value: formatPercent(data?.savingsRateAverage12m, 1) },
  ]

  const ratio = fund?.target?.amount ? (fund.current?.amount ?? 0) / fund.target.amount : 0

  return (
    <Screen
      id="savings"
      state={screenState}
      actions={
        <Link screen="cashFlow" className="btn btn-secondary">
          {t('cashFlow.title')}
        </Link>
      }
    >
      {screenState === 'loading' ? (
        <LoadingRegion>
          <SkeletonCard height="var(--space-xxl)" />
          <SkeletonMetrics count={3} />
        </LoadingRegion>
      ) : screenState === 'error' ? (
        <ErrorState
          titleKey="savings.error.title"
          error={cashFlow.state.status === 'error' ? cashFlow.state.error : undefined}
          onRetry={cashFlow.reload}
        />
      ) : screenState === 'empty' ? (
        <EmptyState titleKey="savings.empty.title" />
      ) : screenState === 'insufficientData' ? (
        <InsufficientData titleKey="savings.insufficientData.title" bodyKey="cashFlow.insufficientData.body" />
      ) : (
        <div className="stack">
          {fund?.target ? (
            <div className="card card-pad-lg row" style={{ gap: 'var(--space-xl)', flexWrap: 'wrap' }}>
              <ProgressRing
                ratio={ratio}
                label={t('savings.goal.emergencyFund')}
                centerLabel={t('savings.goal.emergencyFund')}
                centerValue={formatPercent(ratio)}
              />
              <div className="stack-sm" style={{ flex: 1, minWidth: 0 }}>
                <span className="metric-value">
                  {t('savings.goal.progress', {
                    current: formatMoney(fund.current, { absolute: true }),
                    target: formatMoney(fund.target, { absolute: true }),
                  })}
                </span>
                {fund.monthsCovered !== null && fund.monthsCovered !== undefined ? (
                  <span className="body-secondary">
                    {t('savings.goal.monthsCovered', { count: Math.round(fund.monthsCovered) })}
                  </span>
                ) : null}
                {fund.reached ? <span className="positive">{t('savings.goal.reached')}</span> : null}
                <Button variant="secondary" onClick={() => navigate('settings')}>
                  {t('savings.goal.cta')}
                </Button>
              </div>
            </div>
          ) : (
            <List>
              <GoalRow
                label={t('savings.goal.emergencyFund')}
                progressLabel={t('savings.goal.cta')}
                ratio={0}
              />
            </List>
          )}

          <MetricRow metrics={metrics} />

          <div className="card card-pad-lg">
            <LineChart
              points={complete.map((month) => ({
                label: formatMonthShort(month.month),
                value: month.income.amount > 0 ? ((month.saved?.amount ?? 0) / month.income.amount) * 100 : 0,
              }))}
              caption={t('savings.metric.rate')}
              fromZero
              formatValue={(value) => formatPercent(value / 100, 1)}
            />
          </div>

          {(recommendations.state.data ?? []).slice(0, 2).map((rec) => (
            <RecommendationCard
              key={rec.id}
              recommendation={rec}
              onSelect={() => navigate('recommendationDetail', { id: rec.id })}
            />
          ))}
        </div>
      )}
    </Screen>
  )
}
